/**
 * Sample Backup & Sync content for a signed-in account.
 * Replace this module when the sync and Drive backup APIs are ready.
 * Status, pending changes, auto sync, and Google Drive each have a skeleton.
 */

export type SyncSectionId = 'locations' | 'playlists' | 'itineraries' | 'joystick';

export type AutoInterval = '30m' | '1h' | '4h' | '6h' | '12h' | '24h';

export type DriveFrequency = 'on_change' | 'every_1_hour' | 'every_6_hours' | 'once_a_day';

export type DriveBadgeTone = 'good' | 'warn' | 'danger';

export interface SyncItem {
  id: string;
  label: string;
}

export interface SyncSection {
  id: SyncSectionId;
  title: string;
  items: SyncItem[];
}

export interface AutoIntervalOption {
  id: AutoInterval;
  label: string;
  detail: string;
  /** Phrase used in “checks for unsaved data …”. */
  phrase: string;
  recommended: boolean;
}

export interface DriveFrequencyOption {
  id: DriveFrequency;
  label: string;
  recommended: boolean;
}

export interface DriveSnapshot {
  connected: boolean;
  email: string;
  /** Formatted stamp only. Null means a backup has never finished. */
  lastBackupLabel: string | null;
  /** True when the last backup is inside the last 24 hours. */
  recent: boolean;
  frequency: DriveFrequency;
  /** Empty when the last Drive attempt succeeded. */
  error: string;
}

export interface BackupSyncSnapshot {
  pending: SyncSection[];
  /** Shown after everything on this account is backed up. */
  lastSyncedLabel: string;
  autoSyncEnabled: boolean;
  autoInterval: AutoInterval;
  /** Clock phrase already formatted, such as “4:15 PM today”. */
  nextAutoLabel: string;
  /** Empty when account sync is healthy. */
  syncError: string;
  drive: DriveSnapshot;
}

export const AUTO_INTERVALS: AutoIntervalOption[] = [
  {
    id: '30m',
    label: 'Every 30 minutes',
    detail: 'Balanced option for active daily use.',
    phrase: 'every 30 minutes',
    recommended: false,
  },
  {
    id: '1h',
    label: 'Every 1 hour',
    detail: 'Hourly background sync checks.',
    phrase: 'every 1 hour',
    recommended: false,
  },
  {
    id: '4h',
    label: 'Every 4 hours',
    detail: 'Recommended default — good balance of battery and freshness.',
    phrase: 'every 4 hours',
    recommended: true,
  },
  {
    id: '6h',
    label: 'Every 6 hours',
    detail: 'Four checks per day.',
    phrase: 'every 6 hours',
    recommended: false,
  },
  {
    id: '12h',
    label: 'Every 12 hours',
    detail: 'Twice a day — lighter on battery.',
    phrase: 'every 12 hours',
    recommended: false,
  },
  {
    id: '24h',
    label: 'Every 24 hours',
    detail: 'Once a day — minimal background activity.',
    phrase: 'every 24 hours',
    recommended: false,
  },
];

export const DRIVE_FREQUENCIES: DriveFrequencyOption[] = [
  {
    id: 'on_change',
    label: 'Back up whenever I make changes',
    recommended: true,
  },
  { id: 'every_1_hour', label: 'Every 1 hour', recommended: false },
  { id: 'every_6_hours', label: 'Every 6 hours', recommended: false },
  { id: 'once_a_day', label: 'Once a day', recommended: false },
];

export const DRIVE_BENEFITS = [
  {
    id: 'secure',
    title: 'Secure & private',
    body: 'Your data is stored in your own Google Drive.',
  },
  {
    id: 'anywhere',
    title: 'Access anywhere',
    body: 'Restore your data on any device.',
  },
  {
    id: 'auto',
    title: 'Automatic backup',
    body: 'Choose a backup frequency that works for you.',
  },
] as const;

export const SYNC_HINT = 'Each line below will be backed up when you tap Sync now.';

export const AUTO_SYNC_NOTE =
  'When logged in, pending locations and playlists upload with a status notification. When logged out, you receive a reminder if data still needs syncing. Checks can run a little later to save battery. Notifications must be allowed for reminders to appear while the app is closed.';

export const AUTO_SYNC_OFF_BODY =
  'Turn on auto sync to choose how often MockLoc uploads your locations and playlists — even when the app is closed. Pick a check interval that fits your routine; MockLoc handles the rest while you focus on mocking.';

export const DRIVE_WHAT_BODY =
  'Mock locations (all types), playlists, itineraries, saved places, app settings and more.';

export const DRIVE_PRIVACY =
  'We only access your Google Drive to store and retrieve your app data. Your files remain private.';

export const DRIVE_DISCONNECT_BODY =
  'MockLoc will stop using this Google Drive connection for backups. Your data on this device stays safe. Existing backups in Google Drive are not deleted. Automatic backup will stop until you reconnect.';

export const SAMPLE_BACKUP: BackupSyncSnapshot = {
  lastSyncedLabel: '14 Oct 2026, 6:10 PM',
  autoSyncEnabled: true,
  autoInterval: '4h',
  nextAutoLabel: '4:15 PM today',
  syncError: '',
  pending: [
    {
      id: 'locations',
      title: 'Saved locations',
      items: [
        { id: 'loc-cp', label: 'Location: Connaught Place' },
        { id: 'loc-ig', label: 'Location: India Gate' },
      ],
    },
    {
      id: 'playlists',
      title: 'Playlists',
      items: [
        { id: 'pl-weekend', label: 'Updated playlist: Weekend drive' },
        { id: 'pl-point', label: 'Point: Lodhi Garden' },
      ],
    },
    {
      id: 'itineraries',
      title: 'Automatic itineraries',
      items: [
        { id: 'it-airport', label: 'Itinerary: Airport run' },
        { id: 'it-route', label: 'Route in "Airport run" (driving)' },
      ],
    },
    {
      id: 'joystick',
      title: 'Joystick sessions',
      items: [{ id: 'js-city', label: 'Joystick session: City loop' }],
    },
  ],
  drive: {
    connected: true,
    email: 'user@gmail.com',
    lastBackupLabel: '14 Oct 2026, 6:10 PM',
    recent: false,
    frequency: 'on_change',
    error: '',
  },
};

export function cloneSnapshot(source: BackupSyncSnapshot): BackupSyncSnapshot {
  return {
    ...source,
    syncError: source.syncError || '',
    pending: (source.pending || []).map((section) => ({
      ...section,
      items: (section.items || []).map((item) => ({ ...item })),
    })),
    drive: {
      connected: Boolean(source.drive?.connected),
      email: source.drive?.email || '',
      lastBackupLabel: source.drive?.lastBackupLabel || null,
      recent: Boolean(source.drive?.recent),
      frequency: isDriveFrequency(source.drive?.frequency)
        ? source.drive.frequency
        : 'on_change',
      error: source.drive?.error || '',
    },
    autoInterval: isAutoInterval(source.autoInterval) ? source.autoInterval : '4h',
    autoSyncEnabled: source.autoSyncEnabled !== false,
    nextAutoLabel: source.nextAutoLabel || '',
    lastSyncedLabel: source.lastSyncedLabel || '',
  };
}

export function isAutoInterval(value: string): value is AutoInterval {
  return AUTO_INTERVALS.some((option) => option.id === value);
}

export function isDriveFrequency(value: string | undefined): value is DriveFrequency {
  return DRIVE_FREQUENCIES.some((option) => option.id === value);
}

export function pendingCount(sections: SyncSection[]): number {
  return sections.reduce((total, section) => total + section.items.length, 0);
}

export function visibleSections(sections: SyncSection[]): SyncSection[] {
  return sections.filter((section) => section.items.length > 0);
}

export function intervalOption(id: AutoInterval): AutoIntervalOption {
  return AUTO_INTERVALS.find((option) => option.id === id) ?? AUTO_INTERVALS[2];
}

export function statusTitle(count: number): string {
  return count > 0 ? `${count} pending` : 'All backed up';
}

export function statusCaption(snapshot: BackupSyncSnapshot, count: number): string {
  if (count === 0) {
    const when = snapshot.lastSyncedLabel || 'Just now';
    return `All backed up - ${when}.`;
  }
  if (snapshot.autoSyncEnabled && snapshot.nextAutoLabel) {
    return `to backup, auto trigger at ${snapshot.nextAutoLabel}`;
  }
  return 'to backup';
}

export function backupSummary(count: number): string {
  const noun = count === 1 ? 'item' : 'items';
  return `What will be backed up (${count} ${noun})`;
}

export function sectionHeading(section: SyncSection): string {
  return `${section.title} (${section.items.length})`;
}

export function autoSyncSummary(id: AutoInterval): string {
  return `MockLoc checks for unsaved data ${intervalOption(id).phrase}, even when the app is closed.`;
}

export function lastBackupText(label: string | null): string {
  return label ? `Last backup: ${label}` : 'Last backup: Not yet';
}

export function driveBadge(
  drive: DriveSnapshot,
  backingUp: boolean
): { label: string; tone: DriveBadgeTone } {
  if (!drive.connected) {
    return { label: 'Not Connected', tone: 'danger' };
  }
  if (backingUp) {
    return { label: 'Backup pending', tone: 'warn' };
  }
  if (drive.error) {
    return { label: 'Backup failed', tone: 'danger' };
  }
  if (!drive.lastBackupLabel) {
    return { label: 'Backup pending', tone: 'warn' };
  }
  if (drive.recent) {
    return { label: 'Backed up recently', tone: 'good' };
  }
  return { label: 'Connected', tone: 'good' };
}
